import {
  Component,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TuiButton, TuiIcon, TuiSurface } from '@taiga-ui/core';
import { TuiCardLarge, TuiHeader } from '@taiga-ui/layout';
import { TuiInputModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy';
import { HeaderComponent } from '../../header/header.component';
import { ActivatedRoute, Router } from '@angular/router';
import { PatientListComponent } from '../patient-list/patient-list.component';
import { CodeActiviteService } from '../../services/code-activite.service';

@Component({
  selector: 'app-patient-list-page',
  standalone: true,
  imports: [
    PatientListComponent,
    HeaderComponent,
    TuiSurface,
    TuiHeader,
    TuiCardLarge,
    TuiButton,
    TuiIcon,
    ReactiveFormsModule,
    TuiInputModule,
    TuiTextfieldControllerModule,
  ],
  templateUrl: './patient-list-page.component.html',
  styleUrl: './patient-list-page.component.scss',
})
export class PatientListPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  protected codeActiviteService = inject(CodeActiviteService);
  private readonly formBuilder = inject(FormBuilder);

  // Signaux pour les filtres
  dosActiviteList0: WritableSignal<string[]> = signal(
    this.codeActiviteService.PPC,
  );
  dosEcheanceMin0: WritableSignal<Date | undefined> = signal(undefined);
  dosEcheanceMax0: WritableSignal<Date | undefined> = signal(undefined);
  btnEcheanceSelected0: WritableSignal<number | undefined> = signal(undefined);
  dosEcheanceInitiales0: WritableSignal<boolean> = signal(false);
  nom0: WritableSignal<string | undefined> = signal(undefined);
  page0: WritableSignal<number> = signal(0);

  searchForm!: FormGroup;

  ngOnInit(): void {
    this.searchForm = this.formBuilder.group({
      nom: [''],
    });

    // Restaurer l'état depuis les query params
    this.route.queryParams.subscribe((params) => {
      if (params['echeance'] !== undefined) {
        const echeance = +params['echeance'];
        this.btnEcheanceSelected0.set(echeance);
        this.dosEcheanceInitiales0.set(false);
        this.applyEcheanceFilter(echeance);
      }
      if (params['initiale'] === 'true') {
        this.btnEcheanceSelected0.set(3);
        this.dosEcheanceInitiales0.set(true);
        this.dosEcheanceMin0.set(undefined);
        this.dosEcheanceMax0.set(undefined);
      }
      if (params['nom']) {
        this.nom0.set(params['nom']);
        this.searchForm.patchValue({ nom: params['nom'] });
      }
      if (params['dosActiviteList0']) {
        try {
          const dosActiviteList0 = JSON.parse(params['dosActiviteList0']);
          if (Array.isArray(dosActiviteList0)) {
            this.dosActiviteList0.set(dosActiviteList0);
          }
        } catch (e) {
          console.error(
            'Erreur lors de la désérialisation de dosActiviteList0',
            e,
          );
        }
      }
      if (params['page'] !== undefined) {
        this.page0.set(+params['page']);
      }
    });

    // Écouter les changements du formulaire
    this.searchForm.valueChanges.subscribe(() => {
      if (this.searchForm.valid) {
        this.nom0.set(this.searchForm.value.nom);

        this.updateQueryParams();
      }
    });
  }

  changementDePage(page: number) {
    this.page0.set(page);
    this.updateQueryParams();
  }

  applyEcheanceFilter(echeance?: number): void {
    this.page0.set(0);
    switch (echeance) {
      case 0:
        this.dosEcheanceMin0.set(undefined);
        this.dosEcheanceMax0.set(new Date());
        break;
      case 1:
        const dateMin1 = new Date();
        dateMin1.setDate(dateMin1.getDate() + 1);
        const dateMax1 = new Date();
        dateMax1.setMonth(dateMax1.getMonth() + 1);
        this.dosEcheanceMin0.set(dateMin1);
        this.dosEcheanceMax0.set(dateMax1);
        break;
      case 2:
        const dateMin2 = new Date();
        dateMin2.setMonth(dateMin2.getMonth() + 1);
        dateMin2.setDate(dateMin2.getDate() + 1);
        const dateMax2 = new Date();
        dateMax2.setMonth(dateMax2.getMonth() + 2);
        this.dosEcheanceMin0.set(dateMin2);
        this.dosEcheanceMax0.set(dateMax2);
        break;
      default:
        this.dosEcheanceMin0.set(undefined);
        this.dosEcheanceMax0.set(undefined);
        break;
    }
  }

  updateQueryParams(): void {
    let queryParams: any = {
      nom: this.nom0(),
      dosActiviteList0: JSON.stringify(this.dosActiviteList0()),
      page: this.page0(),
    };
    if (this.dosEcheanceInitiales0()) {
      queryParams.initiale = 'true';
    } else {
      queryParams.initiale = 'false';
    }
    if (this.btnEcheanceSelected0() !== undefined) {
      queryParams.echeance = this.btnEcheanceSelected0();
    } else {
      queryParams.echeance = undefined;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  // Méthodes de sélection des activités
  selectPPC(): void {
    this.dosActiviteList0.set(this.codeActiviteService.PPC);
    this.page0.set(0);
    this.updateQueryParams();
  }

  selectVentilation(): void {
    this.dosActiviteList0.set(this.codeActiviteService.VENTILATION);
    this.page0.set(0);
    this.updateQueryParams();
  }

  selectOxygenotherapie(): void {
    this.dosActiviteList0.set(this.codeActiviteService.O2);
    this.page0.set(0);
    this.updateQueryParams();
  }

  selectForfaitsAssociesPPC(): void {
    this.dosActiviteList0.set(this.codeActiviteService.FORFAITS_ASSOCIES_PPC);
    this.page0.set(0);
    this.updateQueryParams();
  }

  selectForfaitsAssociesVNI(): void {
    this.dosActiviteList0.set(this.codeActiviteService.FORFAITS_ASSOCIES_VNI);
    this.page0.set(0);
    this.updateQueryParams();
  }

  selectAvf(): void {
    this.dosActiviteList0.set(this.codeActiviteService.AVF);
    this.page0.set(0);
    this.updateQueryParams();
  }

  selectAerosols(): void {
    this.dosActiviteList0.set(this.codeActiviteService.AEROSOLS);
    this.page0.set(0);
    this.updateQueryParams();
  }

  unselect(): void {
    this.dosActiviteList0.set([]);
    this.page0.set(0);
    this.updateQueryParams();
  }

  // Méthodes de sélection des échéances
  selectEchues(): void {
    this.btnEcheanceSelected0.set(0);
    this.dosEcheanceInitiales0.set(false);
    this.applyEcheanceFilter(0);
    this.updateQueryParams();
  }

  selectEchues1mois(): void {
    this.btnEcheanceSelected0.set(1);
    this.dosEcheanceInitiales0.set(false);
    this.applyEcheanceFilter(1);
    this.updateQueryParams();
  }

  selectEchues2mois(): void {
    this.btnEcheanceSelected0.set(2);
    this.dosEcheanceInitiales0.set(false);
    this.applyEcheanceFilter(2);
    this.updateQueryParams();
  }

  selectInitiales(): void {
    this.btnEcheanceSelected0.set(3);
    this.dosEcheanceInitiales0.set(true);
    this.dosEcheanceMin0.set(undefined);
    this.dosEcheanceMax0.set(undefined);
    this.applyEcheanceFilter(undefined);
    this.updateQueryParams();
  }

  unselectEchues(): void {
    this.btnEcheanceSelected0.set(undefined);
    this.dosEcheanceInitiales0.set(false);
    this.dosEcheanceMin0.set(undefined);
    this.dosEcheanceMax0.set(undefined);
    this.updateQueryParams();
  }

  onSubmitForm(): void {
    if (this.searchForm.valid) {
      this.nom0.set(this.searchForm.value.nom);
      this.updateQueryParams();
    }
  }

  areArraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  }
}
